// Pure helpers turning the raw (pre-transpose) Houses/Sites readings payload
// — the same `POWER_METERS_READINGS` response PowerComparisonPage already
// fetches via usePowerComparisonReadings, before its `select` transposes it
// into chart series — into standings rows: one per house, with a total for
// the selected period and movement against the prior comparable period.
//
// insight-to-action task 2.2: there is no explicit period selector on this
// page and no prior-period figure in the existing API response, so "the
// selected period" is defined here as the most recent `STANDINGS_PERIOD_DAYS`
// days found in the DAILY_YTD series (the "This Year" chart's data, which
// already covers however much of the year has elapsed) and "the prior
// comparable period" as the `STANDINGS_PERIOD_DAYS` days immediately before
// that — both computed from data the page already has, so no additional
// request is added to the existing endpoint. This is a frontend judgement
// call, not a spec requirement — worth confirming in the click-through
// (task 7.3) alongside the standings' other open framing questions.
import { computePercentChange } from './portfolio/portfolioTransforms.js'

export const STANDINGS_PERIOD_DAYS = 7

/**
 * A house's own page. House Details is keyed by Location id (it falls back
 * to a meter id only for a meter with no location), so this is the id the
 * readings payload already carries for each house.
 */
export function houseDetailsPath(locationId) {
    return `/power/house-details/${encodeURIComponent(locationId)}/`
}

function parseDateOnly(dateString) {
    if (typeof dateString !== 'string') {
        return null
    }
    // Daily readings serialise `date` as YYYY-MM-DD (MeterReadingKWHDailySerializer).
    const parsed = new Date(`${dateString.slice(0, 10)}T00:00:00Z`)
    return Number.isNaN(parsed.getTime()) ? null : parsed
}

function addDaysUTC(date, days) {
    return new Date(date.getTime() + days * 24 * 60 * 60 * 1000)
}

/**
 * Sums a location's `incremental` readings falling within [start, end]
 * (inclusive, UTC day boundaries). Returns `{ total, count }` so a caller can
 * distinguish "zero usage" from "no readings at all" in that window.
 */
function sumIncrementalInRange(points, start, end) {
    let total = 0
    let count = 0

    for (const point of points ?? []) {
        const date = parseDateOnly(point?.date)
        if (!date || date < start || date > end) {
            continue
        }
        const value = Number(point?.incremental)
        if (!Number.isFinite(value)) {
            continue
        }
        total += Math.max(value, 0)
        count += 1
    }

    return { total, count }
}

/**
 * Standard competition ranking (1, 1, 3 — not 1, 1, 2): equal values share a
 * position, and the position after a tie accounts for how many rows tied,
 * so a house is never "ordered arbitrarily" against an equal rival
 * (house-standings requirement "Standings degrade with the data").
 */
export function rankByValue(entries, { valueKey, better = 'lower' } = {}) {
    const sorted = [...entries].sort((a, b) =>
        better === 'lower'
            ? a[valueKey] - b[valueKey]
            : b[valueKey] - a[valueKey]
    )

    let rank = 0
    let previousValue = null
    let seen = 0

    return sorted.map((entry) => {
        seen += 1
        const value = entry[valueKey]
        if (previousValue === null || value !== previousValue) {
            rank = seen
        }
        previousValue = value
        return { ...entry, rank }
    })
}

/**
 * Builds house standings rows from the raw (untransposed) DAILY_YTD series —
 * `[{ id, name, data: [{ date, incremental, cumulative }] }]`, `id` being the
 * house's Location id (carried through as `locationId`) — one row per
 * house/site, ranked by `mode` (see `rankStandingsEntries`; change against the
 * prior period by default), with movement against the prior comparable period. A house with no readings at all in the prior
 * window gets `percentChange: null` rather than an implied movement
 * (house-standings requirement "Newly added house").
 *
 * Returns `{ rows, hasReadingsInPeriod }` — `hasReadingsInPeriod` is `false`
 * when no house has any reading in the current window (or the series is
 * empty), the signal PowerComparisonPage uses to show the shared empty state
 * instead of a table of zeros.
 */
export function buildStandingsRows(
    dailyYtdSeries,
    {
        periodDays = STANDINGS_PERIOD_DAYS,
        mode = DEFAULT_RANK_MODE,
        occupancyByLocationId = {}
    } = {}
) {
    const locations = Array.isArray(dailyYtdSeries) ? dailyYtdSeries : []

    let latest = null
    for (const location of locations) {
        for (const point of location?.data ?? []) {
            const date = parseDateOnly(point?.date)
            if (date && (!latest || date > latest)) {
                latest = date
            }
        }
    }

    if (!latest) {
        return { rows: [], hasReadingsInPeriod: false }
    }

    const currentStart = addDaysUTC(latest, -(periodDays - 1))
    const previousEnd = addDaysUTC(currentStart, -1)
    const previousStart = addDaysUTC(previousEnd, -(periodDays - 1))

    const entries = locations
        .filter((location) => location && typeof location.name === 'string')
        .map((location) => {
            const current = sumIncrementalInRange(
                location.data,
                currentStart,
                latest
            )
            const previous = sumIncrementalInRange(
                location.data,
                previousStart,
                previousEnd
            )

            return {
                name: location.name,
                // The Location's id (MeterReadingAPIView's `keys_and_ids`),
                // which is also what House Details is keyed by.
                locationId: location.id ?? null,
                value: current.total,
                hasCurrentData: current.count > 0,
                previousValue: previous.total,
                // Days with readings in each window, for the change-mode
                // incomplete-previous-week guard (rankStandingsEntries).
                currentCount: current.count,
                previousCount: previous.count
            }
        })

    const hasReadingsInPeriod = entries.some((entry) => entry.hasCurrentData)

    const rows = rankStandingsEntries(entries, {
        mode,
        occupancyByLocationId
    })

    return { rows, hasReadingsInPeriod }
}

// --- Ranking modes (openspec change: property-league-and-house-standings) ----
//
// Kept apart from the window maths above: buildStandingsRows measures each
// house's two windows, and everything below decides what it is ranked by.

/** The ways the standings can be ranked. Every mode is lower-is-better. */
export const RANK_MODES = {
    change: 'change',
    per_occupant: 'per_occupant',
    absolute: 'absolute'
}

/**
 * The default is the size-neutral change against the previous period, in the
 * same sense as the energy quiz house leaderboard (players' average score,
 * quiz/services.py): a small house has no built-in advantage.
 */
export const DEFAULT_RANK_MODE = RANK_MODES.change

/** Why a house is listed below the ranked ones in a mode. */
export const UNRANKED_REASONS = {
    no_current_data: 'no_current_data',
    no_previous_period: 'no_previous_period',
    incomplete_previous_period: 'incomplete_previous_period',
    no_change_figure: 'no_change_figure',
    missing_occupancy: 'missing_occupancy'
}

const isRankMode = (mode) => Object.values(RANK_MODES).includes(mode)

/**
 * A house's figure in mode, or the reason it has none. Change is
 * computePercentChange(current, previous); a house is unranked in that mode
 * without a previous week, or when its previous week has fewer days of readings
 * than this one (a meter outage last week must not become a win this week).
 * Per occupant divides both weeks by the current occupancy.
 */
function measureForMode(entry, mode, occupancyByLocationId) {
    if (!entry.hasCurrentData) {
        return { reason: UNRANKED_REASONS.no_current_data }
    }
    if (mode === RANK_MODES.change) {
        if (!entry.previousCount) {
            return { reason: UNRANKED_REASONS.no_previous_period }
        }
        if (entry.previousCount < entry.currentCount) {
            return { reason: UNRANKED_REASONS.incomplete_previous_period }
        }
        const change = computePercentChange(entry.value, entry.previousValue)
        return change === null
            ? { reason: UNRANKED_REASONS.no_change_figure }
            : { value: change, percentChange: null }
    }
    if (mode === RANK_MODES.per_occupant) {
        const occupancy = Number(occupancyByLocationId?.[entry.locationId])
        if (!(occupancy > 0)) {
            return { reason: UNRANKED_REASONS.missing_occupancy }
        }
        const value = entry.value / occupancy
        return {
            value,
            percentChange: computePercentChange(
                value,
                entry.previousValue / occupancy
            )
        }
    }
    return {
        value: entry.value,
        percentChange: computePercentChange(entry.value, entry.previousValue)
    }
}

/**
 * Ranks measured standings entries in mode (lower is better in every mode, so
 * in change mode the biggest reduction ranks first; ties share a position). A
 * house with no figure in the mode stays in the table below the ranked ones,
 * available: false, with its unrankedReason. Absolute mode reproduces the
 * pre-mode standings exactly.
 */
export function rankStandingsEntries(
    entries,
    { mode = DEFAULT_RANK_MODE, occupancyByLocationId = {} } = {}
) {
    const resolvedMode = isRankMode(mode) ? mode : DEFAULT_RANK_MODE
    const measured = entries.map((entry) => ({
        entry,
        ...measureForMode(entry, resolvedMode, occupancyByLocationId)
    }))

    const ranked = rankByValue(
        measured
            .filter((item) => !item.reason)
            .map((item) => ({ name: item.entry.name, value: item.value })),
        { valueKey: 'value', better: 'lower' }
    )
    const rankByName = new Map(ranked.map((item) => [item.name, item.rank]))

    return (
        measured
            .map(({ entry, reason, value, percentChange }) => ({
                id: entry.name,
                name: entry.name,
                locationId: entry.locationId,
                // In absolute mode an unranked house keeps its (zero) total,
                // as it always has; elsewhere it has no figure.
                value:
                    reason && resolvedMode !== RANK_MODES.absolute
                        ? null
                        : (value ?? entry.value),
                available: !reason,
                rank: reason ? null : rankByName.get(entry.name),
                percentChange: reason ? null : percentChange,
                unrankedReason: reason ?? null,
                hasCurrentData: entry.hasCurrentData
            }))
            // Ranked rows first (rank ascending), unranked rows at the bottom —
            // same convention as buildLeagueRows in portfolioTransforms.js.
            .sort((a, b) => {
                if (a.rank === null && b.rank === null) {
                    return a.name.localeCompare(b.name)
                }
                if (a.rank === null) return 1
                if (b.rank === null) return -1
                return a.rank - b.rank
            })
    )
}

/**
 * The modes to offer: change and total always; per occupant only when at least
 * two houses with readings this period have an occupancy above zero, so there is
 * never an empty normalised table.
 */
export function availableRankModes(rows, occupancyByLocationId = {}) {
    const withOccupancy = (rows ?? []).filter(
        (row) =>
            row.hasCurrentData &&
            Number(occupancyByLocationId?.[row.locationId]) > 0
    )
    return withOccupancy.length >= 2
        ? [RANK_MODES.change, RANK_MODES.per_occupant, RANK_MODES.absolute]
        : [RANK_MODES.change, RANK_MODES.absolute]
}

/** The visible ranked-by statement for mode, worded with the flavour terms. */
export function rankedByLabel(mode, t, periodDays = STANDINGS_PERIOD_DAYS) {
    const house = t('house').toLowerCase()
    if (mode === RANK_MODES.per_occupant) {
        const person = t('pupil')
        return `Ranked by kWh per ${person} over the last ${periodDays} days, lowest first.`
    }
    if (mode === RANK_MODES.absolute) {
        const houses = t('houses').toLowerCase()
        return `Ranked by total kWh over the last ${periodDays} days, lowest first. Larger ${houses} use more.`
    }
    return `Ranked by change against the previous ${periodDays} days, biggest reduction first. Size-neutral: a small ${house} has no built-in advantage.`
}

/** The short label of a mode, for the Rank-by toggle. */
export function rankModeLabel(mode, t) {
    if (mode === RANK_MODES.per_occupant) {
        const person = t('pupil')
        return `kWh per ${person}`
    }
    if (mode === RANK_MODES.absolute) return 'Total kWh'
    return '% change'
}

/** A row's unranked reason as a sentence, or undefined when it is ranked. */
export function unrankedReasonText(
    reason,
    t,
    periodDays = STANDINGS_PERIOD_DAYS
) {
    switch (reason) {
        case UNRANKED_REASONS.no_current_data:
            return `No readings in the last ${periodDays} days.`
        case UNRANKED_REASONS.no_previous_period:
            return `No readings in the previous ${periodDays} days to compare against.`
        case UNRANKED_REASONS.incomplete_previous_period:
            return `The previous ${periodDays} days have fewer days of readings than this week, so the change is not comparable.`
        case UNRANKED_REASONS.no_change_figure:
            return 'No use recorded in one of the two weeks, so there is no change to compare.'
        case UNRANKED_REASONS.missing_occupancy: {
            const people = t('pupils')
            const house = t('house').toLowerCase()
            return `No number of ${people} recorded for this ${house}.`
        }
        default:
            return undefined
    }
}

/** The URL search param the standings ranking mode lives in. */
export const RANK_BY_PARAM = 'rankBy'

/**
 * RankedTable presentation for a standings ranking mode
 * (property-league-and-house-standings). In change mode the figure is itself
 * the movement, so it carries the movement colouring and the separate movement
 * column is hidden.
 */
export function standingsTableProps(mode, t) {
    if (mode === RANK_MODES.change) {
        return {
            unit: '%',
            precision: 1,
            showMovement: false,
            valueColouring: true
        }
    }
    if (mode === RANK_MODES.per_occupant) {
        const person = t('pupil')
        return { unit: `kWh/${person}`, precision: 2 }
    }
    return { unit: 'kWh', precision: 0 }
}
